import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'

import {
    getProcessColumns,
    getProcessFilterItems,
    getProcessFilterToggles,
} from '../components/Processes/process-list-config'
import { getProcesses } from '../services'

interface ProcessListFilters {
    type: string
    status: string
}

const FILTER_DEFAULTS: ProcessListFilters = {
    type: '',
    status: '',
}

export const useProcessList = () => {
    // URL params & form
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<ProcessListFilters>({
        defaults: { sort_by: 'created_at', sort_order: 'desc' },
        filterDefaults: FILTER_DEFAULTS,
    })

    const { control } = useForm<ProcessListFilters>({
        defaultValues: filters,
    })

    // Data fetching
    const fetchFn = useCallback(() => getProcesses(queryParams), [queryParams])

    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_PROCESSES, queryParams],
        queryFn: fetchFn,
        placeholderData: true,
    })

    // Memoized configs
    const columns = useMemo(() => getProcessColumns(), [])
    const filterItems = useMemo(() => getProcessFilterItems(), [])
    const filterToggles = useMemo(() => getProcessFilterToggles(), [])

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        processes: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        control,
        setFilter,
        params,
        searchInputProps,

        columns,
        filterItems,
        filterToggles,

        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        refetch,
    }
}
